import React, { useCallback, useRef } from 'react';

import update from 'immutability-helper';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { EMPTY_ILLUSTRATIONS, EmptyContainer, Table } from '@thinknet-gmbh/platform-common-ui';
import { DndProvider, useDrag, useDrop } from 'react-dnd';

import type { ColumnsType } from 'antd/es/table';


interface DraggableBodyRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
}

const type = 'DraggableBodyRow';

const DraggableBodyRow = ({
  index,
  moveRow,
  className,
  style,
  ...restProps
}: DraggableBodyRowProps) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: monitor => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: { index: number }) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};



const DraggableTable = ({ data, setData, loading, t, columns, children, onRowUpdateAttr, emptyTitle}: {
  columns?: ColumnsType<any>
  data: any[]
  setData: (data: any) => any
  loading?: boolean
  t: Function
  children?: React.ReactNode
  onRowUpdateAttr?: (data: any, attr: any) => React.HTMLAttributes<any>
  emptyTitle?: string
}) => {
  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragRow = data[dragIndex];
      setData(
        update(data, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        }),
      );
    },
    [data, setData],
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <Table
        t={t}
        {...columns && columns}
        loading={loading}
        dataSource={data}
        pagination={false}
        components={components}
        onRow={(_, index) => {
          const attr = {
            index,
            moveRow,
          };
          if(onRowUpdateAttr){
            return onRowUpdateAttr(_, attr)
          }
          return attr as React.HTMLAttributes<any>;
        }}
        locale={{
          ...emptyTitle && {
            emptyText: (
              <EmptyContainer
                illustration={EMPTY_ILLUSTRATIONS.dataAnalysis}
                emptyTitle={emptyTitle}
              />
            ),
          },
        }}
      >
        {children}
      </Table>
    </DndProvider>
  );
};

export default DraggableTable;
